import React, { useState, useEffect } from 'react';
import { Modal, Dropdown, MenuProps } from 'antd';
import { motion, AnimatePresence, AnimatePresenceProps } from 'framer-motion';
import { ChevronLeft, Globe, ChevronDown } from 'lucide-react';
import { releaseData } from '@/config/release.config';
import {
  POLICY_CONTENT,
  TERMS_CONTENT,
  POLICY_LANG_LABELS,
  PolicyLang,
} from '@/config/privacy_policy_and_term_condition.config';
import { useAppStateStore } from '@/zusttandStore/appState.store';
import { AnimatedCardStatusList } from '@/components/ui/card-status-list';
import type { Card } from '@/components/ui/card-status-list';
import logoIcon from '@/assets/images/FROMAS_NEW_ICON.png';
import moment from 'moment';
import { useScreenSize } from '@/hooks/useScreenSize';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { getSubscriptionInfo } from '@/apiClient/apiClient';
import { useSelector } from 'react-redux';
import { AppState } from 'ReducerState';

const AnimatePresenceCompat = AnimatePresence as React.ComponentType<
  AnimatePresenceProps & { children: React.ReactNode }
>;

type View = 'main' | 'terms' | 'privacy' | 'history' | 'subscription';

const getLocaleLang = (): PolicyLang => {
  try {
    const raw = localStorage.getItem('locale');
    const data = raw ? (JSON.parse(raw) as { locale?: string }) : null;
    if (data?.locale === 'th') return 'th';
    if (data?.locale === 'zh') return 'ch';
  } catch {
    // ignore
  }
  return 'en';
};

const proseClass = `
  [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-slate-800 [&_h2]:mb-5 [&_h2]:tracking-tight
  [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:font-semibold [&_h3]:text-slate-800 [&_h3]:mt-8 [&_h3]:mb-4 [&_h3]:tracking-tight
  [&_p]:text-[15px] [&_p]:md:text-[16px] [&_p]:text-slate-600 [&_p]:leading-relaxed [&_p]:mb-5
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:md:pl-6 [&_ol]:space-y-4 [&_ol]:mb-6
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:md:pl-6 [&_ul]:space-y-3 [&_ul]:mt-3
  [&_li]:text-[15px] [&_li]:md:text-[16px] [&_li]:text-slate-600 [&_li]:leading-relaxed
  [&_strong]:font-semibold [&_strong]:text-slate-800
  [&_a]:text-blue-500 [&_a]:underline [&_a:hover]:text-blue-600
`;

const AboutModal = () => {
  const { openAboutModal, setOpenAboutModal } = useAppStateStore();
  const { isDesktop, isTablet } = useScreenSize();
  const { authUser } = useSelector((state: AppState) => state.auth);
  const canViewNextInvoice = ['S', 'P', 'G'].includes(authUser?.user_group_type ?? '');
  const [view, setView] = useState<View>('main');
  const [lang, setLang] = useState<PolicyLang>('en');

  const { data: subscriptionData } = useQuery({
    queryKey: ['getSubscriptionInfo', openAboutModal],
    queryFn: () => getSubscriptionInfo(),
    enabled: !!openAboutModal,
  });

  useEffect(() => {
    if (openAboutModal) {
      setView('main');
      setLang(getLocaleLang());
    }
  }, [openAboutModal]);

  const latestRelease = releaseData['en']?.[0];
  const version = latestRelease?.version ?? process.env.REACT_APP_VERSION ?? '';
  const lastUpdateDate = latestRelease?.release_date
    ? moment(latestRelease.release_date).format('DD/MM/YYYY')
    : '';

  const langItems: MenuProps['items'] = (
    Object.entries(POLICY_LANG_LABELS) as [PolicyLang, string][]
  ).map(([key, label]) => ({
    key,
    label: (
      <div
        className={`px-2 py-1 text-sm font-medium ${lang === key ? 'text-blue-600' : 'text-slate-700'}`}
      >
        {label}
      </div>
    ),
    onClick: () => setLang(key as PolicyLang),
  }));

  const modalWidth =
    view === 'main' ? 560 : view === 'history' ? (isDesktop || isTablet ? '65%' : '90%') : isDesktop || isTablet ? 780 : '90%';

  // ── Main view ────────────────────────────────────────────────────────
  const MainView = () => (
    <div className='flex flex-col items-center py-8 px-6 gap-6'>
      <div className='flex flex-row items-center gap-5'>
        <img
          src='/FROMAS_S.png'
          alt='Fromas'
          className='w-[100px] h-[100px] object-contain flex-shrink-0'
        />
        <div>
          <h2 className='text-[42px] font-black tracking-widest text-gray-900 leading-none'>
            FROMAS
          </h2>
          <p className='text-gray-600 font-medium mt-2 text-lg'>Cloud Version {version}</p>
          <p className='text-gray-400 text-sm mt-0.5'>(Last update: {lastUpdateDate})</p>
        </div>
      </div>

      <div
        className='w-full max-w-sm rounded-2xl border px-6 py-5 divide-y'
        style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}
      >
        <div className='flex items-center justify-between py-2.5 first:pt-0'>
          <span className='font-semibold text-gray-700 text-sm'>Max. Room</span>
          <span className='text-gray-600 text-sm'>
            {subscriptionData?.data?.result_data?.subscription?.product?.qty ?? '-'}{' '}
            {subscriptionData?.data?.result_data?.subscription?.product?.unit_name ?? 'Rooms'}
          </span>
        </div>
        <div className='flex items-center justify-between py-2.5'>
          <span className='font-semibold text-gray-700 text-sm'>Register Date</span>
          <span className='text-gray-600 text-sm'>
            {subscriptionData?.data?.result_data?.subscription?.start_at
              ? moment(subscriptionData?.data?.result_data?.subscription?.start_at).format(
                  'DD/MM/YYYY'
                )
              : '-'}
          </span>
        </div>
        {/* <div className='flex items-center justify-between py-2.5 last:pb-0'>
          <span className='font-semibold text-gray-700 text-sm'>Expire Date</span>
          <span className='text-gray-600 text-sm'>
            {license_info?.expire_date ? dayjs(license_info.expire_date).format('DD/MM/YYYY') : '-'}
          </span>
        </div> */}
      </div>

      <div className='flex items-center gap-3 text-sm flex-wrap justify-center'>
        <button
          onClick={() => setView('terms')}
          className='text-blue-500 hover:text-blue-700 font-medium transition-colors underline-offset-2 hover:underline'
        >
          Terms of use
        </button>
        <span className='text-gray-300 select-none'>|</span>
        <button
          onClick={() => setView('privacy')}
          className='text-blue-500 hover:text-blue-700 font-medium transition-colors underline-offset-2 hover:underline'
        >
          Privacy Policies
        </button>
        <span className='text-gray-300 select-none'>|</span>
        <button
          onClick={() => setView('history')}
          className='text-blue-500 hover:text-blue-700 font-medium transition-colors underline-offset-2 hover:underline'
        >
          Version History
        </button>
        {subscriptionData?.data?.result_data?.subscription && (
          <>
            <span className='text-gray-300 select-none'>|</span>
            <button
              onClick={() => setView('subscription')}
              className='text-blue-500 hover:text-blue-700 font-medium transition-colors underline-offset-2 hover:underline'
            >
              Subscription Info
            </button>
          </>
        )}
      </div>

      <p className='text-xs text-gray-400 text-center'>
        Copyright © 2019-{new Date().getFullYear()} Newsoft Technologies Consultants. Allright
        Reserved.
      </p>
    </div>
  );

  // ── Sub-view header ──────────────────────────────────────────────────
  const SubHeader = ({ title, showLang = true }: { title: string; showLang?: boolean }) => (
    <div
      className='flex items-center justify-between px-5 py-3.5'
      style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
    >
      <button
        onClick={() => setView('main')}
        className='flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition-colors'
      >
        <ChevronLeft className='w-4 h-4' />
        Back
      </button>
      <span className='font-semibold text-slate-700 text-sm'>{title}</span>
      {showLang ? (
        <Dropdown menu={{ items: langItems }} trigger={['click']} placement='bottomRight'>
          <button className='flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-2.5 py-1.5 transition-colors cursor-pointer'>
            <Globe className='w-3.5 h-3.5' />
            {POLICY_LANG_LABELS[lang]}
            <ChevronDown className='w-3 h-3' />
          </button>
        </Dropdown>
      ) : (
        <div className='w-16' />
      )}
    </div>
  );

  // ── Terms view ────────────────────────────────────────────────────────
  const TermsView = () => (
    <div>
      <SubHeader title='Terms of Use' />
      <div className='max-h-[65vh] overflow-y-auto about-scrollbar px-8 py-6'>
        <div
          className={proseClass}
          dangerouslySetInnerHTML={{ __html: TERMS_CONTENT[lang] || TERMS_CONTENT['en'] }}
        />
      </div>
    </div>
  );

  // ── Privacy view ─────────────────────────────────────────────────────
  const PrivacyView = () => (
    <div>
      <SubHeader title='Privacy Policy' />
      <div className='max-h-[65vh] overflow-y-auto about-scrollbar px-8 py-6'>
        <div
          className={proseClass}
          dangerouslySetInnerHTML={{ __html: POLICY_CONTENT[lang] || POLICY_CONTENT['en'] }}
        />
      </div>
    </div>
  );

  // ── Build full cards with sub_features + images (mirrors ReleaseModal) ──
  const buildFullCards = (
    releaseItems: {
      release_feature: string;
      sub_feature?: string[];
      image?: string;
      status?: string;
    }[]
  ): Card[] =>
    releaseItems.map((item, i) => {
      let subFeatureContent: React.ReactNode = null;

      if (item.sub_feature && item.sub_feature.length > 0) {
        subFeatureContent = (
          <div className='flex flex-col mt-1'>
            {item.sub_feature.map((feat, idx) => {
              const match = feat.match(/^(.*?)(\[.+\])$/);
              let text = feat;
              let badge: string | null = null;

              if (match) {
                text = match[1].trim();
                badge = match[2].trim();
              } else if (feat.trim().startsWith('[')) {
                text = '';
                badge = feat.trim();
              }

              const isBullet =
                text.trim().startsWith('สามารถ') ||
                text.trim().startsWith('Transfer') ||
                text.trim().startsWith('Move') ||
                text.trim().startsWith('Block');

              if (!text && badge) {
                return (
                  <div key={idx} className='mt-1 mb-2'>
                    <span
                      style={{
                        color: '#0958d9',
                        backgroundColor: '#e6f7ff',
                        border: '1px solid #91d5ff',
                      }}
                      className='text-xs font-medium px-2 py-0.5 rounded-sm inline-block shadow-sm'
                    >
                      {badge}
                    </span>
                  </div>
                );
              }

              return (
                <div key={idx} className='flex flex-col gap-1 mb-1.5'>
                  <div className={`flex items-start ${isBullet ? 'gap-1.5' : 'gap-0'}`}>
                    <span className='text-xs mt-[1.5px] text-gray-400 mr-1'>●</span>
                    <span className='flex-1 leading-relaxed text-black/75 text-sm'>{text}</span>
                  </div>
                  {badge && (
                    <div className={isBullet ? 'pl-3.5 mt-0.5' : 'mt-0.5'}>
                      <span
                        style={{
                          color: '#0958d9',
                          backgroundColor: '#e6f7ff',
                          border: '1px solid #91d5ff',
                        }}
                        className='text-xs font-medium px-2 py-0.5 rounded-sm inline-block shadow-sm'
                      >
                        {badge}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      let imageContent: React.ReactNode = null;
      if (item.image) {
        imageContent = (
          <div
            className='mt-2.5 mb-1 overflow-hidden rounded-md border shadow-[0_2px_8px_rgba(0,0,0,0.04)] bg-white p-1 w-full'
            style={{ borderColor: 'rgba(3,143,222,0.15)' }}
          >
            <img
              src={item.image}
              alt={item.release_feature}
              className='w-full h-auto object-contain rounded shadow-sm bg-gray-50/50'
              style={{ border: '1px solid rgba(0,0,0,0.03)' }}
            />
          </div>
        );
      }

      const hasDescription = subFeatureContent || imageContent;
      const descriptionContent = hasDescription ? (
        <div className='flex flex-col'>
          {subFeatureContent}
          {imageContent}
        </div>
      ) : undefined;

      return {
        id: String(i + 1),
        title: item.release_feature,
        status: (item.status as Card['status']) ?? 'completed',
        description: descriptionContent,
      };
    });

  // ── Subscription Info view ───────────────────────────────────────────
  const SubscriptionView = () => {
    const sub = subscriptionData?.data?.result_data?.subscription;

    const statusColor =
      sub?.status === 'active'
        ? { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' }
        : sub?.status === 'trialing'
          ? { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' }
          : { bg: 'bg-red-100', text: 'text-red-600', dot: 'bg-red-400' };

    const fmt = (d?: string | null) => (d ? dayjs(d).format('DD MMM YYYY') : '-');

    const billingLabel = (() => {
      const cycle = sub?.plan.billing_cycle ?? '';
      const val = sub?.plan.billing_cycle_value ?? 1;
      if (cycle === 'monthly' || cycle === 'month') {
        return `Every ${val > 1 ? val + ' ' : ''}Month${val > 1 ? 's' : ''}`;
      }
      if (cycle === 'yearly' || cycle === 'year') {
        return `Every ${val > 1 ? val + ' ' : ''}Year${val > 1 ? 's' : ''}`;
      }
      return cycle || '-';
    })();

    if (!sub) {
      return (
        <div>
          <SubHeader title='Subscription Info' showLang={false} />
          <div className='px-8 py-10 flex flex-col items-center gap-2'>
            <span className='text-3xl'>📋</span>
            <p className='text-gray-400 text-sm'>No subscription data available.</p>
          </div>
        </div>
      );
    }

    return (
      <div>
        <SubHeader title='Subscription Info' showLang={false} />
        <div className='px-6 py-5 flex flex-col gap-4'>
          {/* Plan header card */}
          <div
            className='rounded-xl px-5 py-4 flex items-center justify-between'
            style={{
              background: 'linear-gradient(135deg, #e8f4fd 0%, #f0f9ff 100%)',
              border: '1px solid rgba(3,143,222,0.15)',
            }}
          >
            <div>
              <p className='text-xs text-blue-400 font-medium uppercase tracking-wider mb-0.5'>
                Plan
              </p>
              <p className='text-lg font-bold text-gray-800'>{sub.plan.name}</p>
              <p className='text-xs text-gray-500 mt-0.5'>{billingLabel}</p>
            </div>
            <div className='flex flex-col items-end gap-2'>
              <span
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${statusColor.bg} ${statusColor.text}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${statusColor.dot}`} />
                {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
              </span>
            </div>
          </div>

          {/* Stat boxes */}
          <div className='grid grid-cols-2 gap-3'>
            {[
              { label: 'Max Rooms', value: sub.product.qty, unit: 'rooms' },
              { label: 'Auto Renew', value: sub.auto_renew ? 'Yes' : 'No', unit: null },
            ].map(({ label, value, unit }) => (
              <div
                key={label}
                className='rounded-xl px-4 py-3 flex flex-col items-center gap-0.5 text-center'
                style={{ background: 'rgba(0,0,0,0.025)', border: '1px solid rgba(0,0,0,0.07)' }}
              >
                <p className='text-xs text-gray-400 font-medium'>{label}</p>
                <p className='text-base font-bold text-gray-800'>{value}</p>
                {unit && <p className='text-[10px] text-gray-400'>{unit}</p>}
              </div>
            ))}
          </div>

          {/* Date rows */}
          <div
            className='rounded-xl border divide-y overflow-hidden'
            style={{ borderColor: 'rgba(0,0,0,0.08)' }}
          >
            {[
              { label: 'Start Date', value: fmt(sub.start_at) },
              { label: 'Period Start', value: fmt(sub.current_period_start) },
              { label: 'Period End', value: fmt(sub.current_period_end) },
              ...(sub.trial_end ? [{ label: 'Trial Ends', value: fmt(sub.trial_end) }] : []),
              ...(sub.cancel_at ? [{ label: 'Cancels At', value: fmt(sub.cancel_at) }] : []),
            ].map(({ label, value }) => (
              <div
                key={label}
                className='flex items-center justify-between px-4 py-2.5'
                style={{ background: 'rgba(0,0,0,0.01)' }}
              >
                <span className='text-sm text-gray-500'>{label}</span>
                <span className='text-sm font-medium text-gray-700'>{value}</span>
              </div>
            ))}
          </div>

          {/* Add-on details */}
          {sub.details.length > 0 && (
            <div
              className='rounded-xl border overflow-hidden'
              style={{ borderColor: 'rgba(0,0,0,0.08)' }}
            >
              <div
                className='px-4 py-2.5'
                style={{
                  background: 'rgba(0,0,0,0.03)',
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                }}
              >
                <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                  Add-ons
                </p>
              </div>
              <div className='divide-y' style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                {[...sub.details]
                  .sort((a, b) => (a.sort_no ?? 0) - (b.sort_no ?? 0))
                  .map((item) => {
                    return (
                      <div
                        key={item.product_id}
                        className='flex items-center justify-between px-4 py-2.5'
                        style={{ background: 'rgba(0,0,0,0.01)' }}
                      >
                        <p className='text-sm text-gray-700 font-medium'>{item.product_name}</p>
                        <span className='text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-600'>
                          {item.qty} {item.unit_name}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Next invoice */}
          {canViewNextInvoice && sub.next_invoice && (
            <div
              className='rounded-xl px-5 py-4'
              style={{
                background: 'rgba(251,191,36,0.07)',
                border: '1px solid rgba(251,191,36,0.25)',
              }}
            >
              <p className='text-xs font-semibold text-yellow-700 uppercase tracking-wider mb-3'>
                Next Invoice
              </p>
              <div className='flex items-center justify-between'>
                <div className='flex flex-col gap-1'>
                  <p className='text-sm text-gray-500'>
                    Due:{' '}
                    <span className='font-medium text-gray-700'>
                      {fmt(sub.next_invoice.due_date)}
                    </span>
                  </p>
                  <p className='text-sm text-gray-500'>
                    Amount:{' '}
                    <span className='font-bold text-gray-800'>
                      {sub.next_invoice.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}{' '}
                      {sub.currency.toUpperCase()}
                    </span>
                  </p>
                </div>
                {sub.next_invoice.payment_link_url &&
                  sub.next_invoice.payment_link_status !== 'expired' && (
                    <a
                      href={sub.next_invoice.payment_link_url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-xs font-semibold px-4 py-2 rounded-lg text-white transition-colors'
                      style={{ background: '#038fde' }}
                    >
                      Pay Now
                    </a>
                  )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Version History view ─────────────────────────────────────────────
  const HistoryView = () => {
    const langKey = lang === 'ch' ? 'en' : (lang as 'en' | 'th');
    const releases = releaseData[langKey] || releaseData['en'];

    return (
      <div>
        <SubHeader title='Version History' showLang />
        <div className='max-h-[65vh] overflow-y-auto about-scrollbar csl-scrollbar p-4 space-y-6'>
          {releases.map((release) => {
            const cards = buildFullCards(release.release_data);
            const dateLabel = release.release_date
              ? moment(release.release_date).format('DD/MM/YYYY')
              : '';

            return (
              <div key={release.version}>
                <div
                  className='flex items-center gap-2 mb-3 px-3 py-2 rounded-lg'
                  style={{ background: 'rgba(3,143,222,0.06)', borderLeft: '3px solid #038fde' }}
                >
                  <span
                    className='text-sm font-bold px-3 py-0.5 rounded-full'
                    style={{ background: 'rgba(3,143,222,0.15)', color: '#038fde' }}
                  >
                    v{release.version}
                  </span>
                  <span className='text-sm text-gray-500 font-medium'>{dateLabel}</span>
                  <span
                    className='ml-auto text-sm font-medium px-2 py-0.5 rounded-full'
                    style={{ background: 'rgba(3,143,222,0.08)', color: '#038fde' }}
                  >
                    {cards.length} features
                  </span>
                </div>
                <AnimatedCardStatusList title={`v${release.version}`} cards={cards} />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={
        <span className='font-bold' style={{ color: '#038fde', fontSize: 15 }}>
          About
        </span>
      }
      open={openAboutModal}
      closable
      maskClosable
      onCancel={() => setOpenAboutModal(false)}
      footer={null}
      width={modalWidth}
      styles={{ body: { padding: 0 } }}
      destroyOnClose
    >
      <style>{`
        .about-scrollbar::-webkit-scrollbar { width: 6px; }
        .about-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .about-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
        .about-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
      `}</style>

      <AnimatePresenceCompat>
        {view === 'main' && (
          <motion.div
            key='main'
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18 }}
          >
            <MainView />
          </motion.div>
        )}

        {view === 'terms' && (
          <motion.div
            key='terms'
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ duration: 0.18 }}
          >
            <TermsView />
          </motion.div>
        )}

        {view === 'privacy' && (
          <motion.div
            key='privacy'
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ duration: 0.18 }}
          >
            <PrivacyView />
          </motion.div>
        )}

        {view === 'history' && (
          <motion.div
            key='history'
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ duration: 0.18 }}
          >
            <HistoryView />
          </motion.div>
        )}

        {view === 'subscription' && (
          <motion.div
            key='subscription'
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ duration: 0.18 }}
          >
            <SubscriptionView />
          </motion.div>
        )}
      </AnimatePresenceCompat>
    </Modal>
  );
};

export default AboutModal;
